import React, { useRef, useEffect, useState } from 'react';
import { Modal, View, Text, TextInput, Pressable, StyleSheet, ScrollView } from 'react-native';

interface PhoneVerificationModalProps {
    visible: boolean;
    onClose: () => void;
    onVerify: () => void;
    onResend: () => void;
    isLoading: boolean;
    verificationCode: string;
    setVerificationCode: (v: string) => void;
    phoneE164: string;
    canVerify?: boolean;
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
    visible,
    onClose,
    onVerify,
    onResend,
    isLoading,
    verificationCode,
    setVerificationCode,
    phoneE164,
    canVerify = true,
}) => {
    const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
    const inputRefs = useRef<(TextInput | null)[]>([]);

    // Sync internal digits state with parent verificationCode
    useEffect(() => {
        if (verificationCode.length === 0) {
            setDigits(['', '', '', '', '', '']);
        } else if (verificationCode.length <= 6) {
            const newDigits = verificationCode.split('');
            while (newDigits.length < 6) {
                newDigits.push('');
            }
            setDigits(newDigits);
        }
    }, [verificationCode]);

    // Auto-focus first input when modal becomes visible
    useEffect(() => {
        if (visible) {
            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        }
    }, [visible]);

    const handleDigitChange = (index: number, value: string) => {
        // Only allow numbers
        const sanitized = value.replace(/[^0-9]/g, '');
        
        if (sanitized.length > 1) {
            // Handle paste of multiple digits
            const pastedDigits = sanitized.slice(0, 6).split('');
            const newDigits = [...digits];
            pastedDigits.forEach((digit, i) => {
                if (index + i < 6) {
                    newDigits[index + i] = digit;
                }
            });
            setDigits(newDigits);
            setVerificationCode(newDigits.join(''));
            
            // Focus the next empty box or the last one
            const nextIndex = Math.min(index + pastedDigits.length, 5);
            inputRefs.current[nextIndex]?.focus();
        } else {
            // Single digit input
            const newDigits = [...digits];
            newDigits[index] = sanitized;
            setDigits(newDigits);
            setVerificationCode(newDigits.join(''));

            // Auto-focus next input if digit was entered
            if (sanitized && index < 5) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyPress = (index: number, key: string) => {
        if (key === 'Backspace') {
            if (!digits[index] && index > 0) {
                // If current box is empty and backspace is pressed, go to previous box
                inputRefs.current[index - 1]?.focus();
            }
        }
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.overlay}>
                <View style={styles.modal}>
                    <ScrollView 
                        contentContainerStyle={styles.content}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={true}
                    >
                        <Text style={styles.title}>Enter verification code</Text>
                        <Text style={styles.subtitle}>
                            {!canVerify 
                                ? `Sending code to ${phoneE164}...` 
                                : `We sent a code to ${phoneE164}`
                            }
                        </Text>
                        
                        <View style={styles.inputContainer}>
                            <Text style={styles.inputLabel}>Verification code</Text>
                            <View style={styles.otpContainer}>
                                {digits.map((digit, index) => (
                                    <TextInput
                                        key={index}
                                        ref={(ref) => (inputRefs.current[index] = ref)}
                                        style={[
                                            styles.otpBox,
                                            digit ? styles.otpBoxFilled : null,
                                            !canVerify && styles.otpBoxDisabled
                                        ]}
                                        value={digit}
                                        onChangeText={(value) => handleDigitChange(index, value)}
                                        onKeyPress={({ nativeEvent }) => handleKeyPress(index, nativeEvent.key)}
                                        keyboardType="number-pad"
                                        maxLength={1}
                                        selectTextOnFocus
                                        editable={canVerify && !isLoading}
                                    />
                                ))}
                            </View>
                        </View>

                        <Pressable 
                            style={[styles.primaryButton, (!canVerify || isLoading) && styles.disabled]} 
                            disabled={!canVerify || isLoading} 
                            onPress={onVerify}
                        >
                            <Text style={styles.primaryButtonText}>
                                {!canVerify ? 'Sending...' : isLoading ? 'Verifying...' : 'Verify'}
                            </Text>
                        </Pressable>
                        <Pressable style={styles.secondaryButton} onPress={onResend} disabled={isLoading}>
                            <Text style={styles.secondaryButtonText}>Resend code</Text>
                        </Pressable>
                        <Pressable style={styles.linkButton} onPress={onClose}>
                            <Text style={styles.linkText}>← Back</Text>
                        </Pressable>
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '90%',
        maxWidth: 480,
        maxHeight: '90%',
    },
    content: {
        padding: 24,
        flexGrow: 1,
    },
    title: {
        fontSize: 18,
        fontWeight: '700',
        color: '#222',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 14,
        color: '#555',
        marginBottom: 16,
        textAlign: 'center',
    },
    inputContainer: {
        marginBottom: 24,
    },
    inputLabel: {
        fontSize: 12,
        color: '#717171',
        marginBottom: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        textAlign: 'center',
    },
    otpContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
    },
    otpBox: {
        width: 48,
        height: 56,
        borderWidth: 2,
        borderColor: '#D0D0D0',
        borderRadius: 12,
        fontSize: 24,
        fontWeight: '600',
        color: '#222',
        textAlign: 'center',
        backgroundColor: '#F8F9FA',
    },
    otpBoxFilled: {
        borderColor: '#3C8248',
        backgroundColor: '#FFFFFF',
    },
    otpBoxDisabled: {
        opacity: 0.5,
    },
    primaryButton: {
        backgroundColor: '#3C8248',
        borderRadius: 8,
        paddingVertical: 14,
        alignItems: 'center',
        marginBottom: 12,
    },
    primaryButtonText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
    },
    secondaryButton: {
        borderWidth: 1,
        borderColor: '#222',
        borderRadius: 8,
        paddingVertical: 12,
        alignItems: 'center',
        marginBottom: 8,
    },
    secondaryButtonText: {
        color: '#222',
        fontSize: 14,
        fontWeight: '600',
    },
    linkButton: {
        alignItems: 'center',
        paddingVertical: 8,
    },
    linkText: {
        color: '#3C8248',
        fontSize: 14,
        fontWeight: '500',
    },
    disabled: {
        opacity: 0.6,
    },
});

export default PhoneVerificationModal;

